import {
  CmsContentAboutHeroSchema,
  type CmsContentAboutHeroForm,
} from "@/modules/cms/domain/contracts";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import { Button, FormCheckbox, FormRoot, RHF, zodResolver } from "@devhop/ui";
import { useCallback, useState } from "react";
import type { Resolver, UseFormReturn } from "react-hook-form";

const IMAGE_KEY_PREFIX = "uploads/cms/about-hero";

type AboutHeroFormProps = {
  content: CmsContentAboutHeroForm | null;
  onSubmit: (content: CmsContentAboutHeroForm) => void;
  onCancel: () => void;
  submitting?: boolean;
};

const defaultContent: CmsContentAboutHeroForm = {
  imageKey: "",
  isActive: true,
};

export function AboutHeroForm({
  content,
  onSubmit,
  onCancel,
  submitting,
}: AboutHeroFormProps) {
  const methods = RHF.useForm<CmsContentAboutHeroForm>({
    resolver: zodResolver(
      CmsContentAboutHeroSchema,
    ) as Resolver<CmsContentAboutHeroForm>,
    defaultValues: content ?? defaultContent,
  });
  const watchImageKey = methods.watch("imageKey") ?? "";
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);

  const handleSubmit = useCallback(
    async (vals: CmsContentAboutHeroForm) => {
      const data = { ...vals };
      if (pendingFile) {
        setUploading(true);
        try {
          data.imageKey = await uploadImageToKey(IMAGE_KEY_PREFIX, pendingFile);
        } finally {
          setUploading(false);
        }
      }
      onSubmit(data);
    },
    [pendingFile, onSubmit],
  );

  return (
    <FormRoot<CmsContentAboutHeroForm>
      methods={methods as UseFormReturn<CmsContentAboutHeroForm>}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <ImageKeyUploadField
          keyPrefix={IMAGE_KEY_PREFIX}
          label="ຮູບ Hero (16:9)"
          aspectHint="ອັດສ່ວນ 16:9"
          aspectRatio="aspect-video"
          widthPx={1280}
          heightPx={720}
          value={watchImageKey}
          onChange={(key) =>
            methods.setValue("imageKey", key, { shouldDirty: true })
          }
          disabled={submitting}
          deferUpload
          pendingFile={pendingFile}
          onFileSelect={setPendingFile}
          onClear={() => {
            methods.setValue("imageKey", "", { shouldDirty: true });
            setPendingFile(null);
          }}
        />
        <FormCheckbox name="isActive" label="ເປີດໃຊ້ງານ" />
        <div className="flex gap-2 pt-2">
          <Button type="submit" disabled={submitting || uploading}>
            {uploading ? "ກຳລັງອັບໂຫຼດ..." : submitting ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
          <Button type="button" variant="outline" onClick={onCancel}>
            ຍົກເລີກ
          </Button>
        </div>
      </div>
    </FormRoot>
  );
}
